import { useState, useMemo, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { format } from "date-fns";
import {
  Timer,
  Clock,
  CheckCircle2,
  XCircle,
  ChevronDown,
  ChevronUp,
  Loader2,
} from "lucide-react";
import { useAuth } from "../../../hooks/useAuth";
import { useAdminWorkAdjustments, useReviewWorkAdjustment } from "../../../hooks/useWorkAdjustment";
import { Modal } from "../../../components/ui/Modal";
import { Button } from "../../../components/ui/Button";
import { LoadingSpinner } from "../../../components/ui/LoadingSpinner";
import type {
  WorkAdjustmentRequest,
  WorkAdjustmentStatus,
  WorkAdjustmentType,
  HolidayType,
} from "../../../lib/workAdjustmentApi";

// ── Constants ─────────────────────────────────────────────────────────────────

const STATUS_TABS: { key: WorkAdjustmentStatus; label: string }[] = [
  { key: "PENDING",  label: "Pending"  },
  { key: "APPROVED", label: "Approved" },
  { key: "REJECTED", label: "Rejected" },
];

const STATUS_CONFIG: Record<
  WorkAdjustmentStatus,
  { badge: string; border: string; icon: typeof Clock }
> = {
  PENDING:  { badge: "bg-[#fef9c3] text-[#a16207]", border: "border-l-[#f59e0b]", icon: Clock        },
  APPROVED: { badge: "bg-[#dcfce7] text-[#15803d]", border: "border-l-[#16a34a]", icon: CheckCircle2 },
  REJECTED: { badge: "bg-[#fee2e2] text-[#b91c1c]", border: "border-l-[#dc2626]", icon: XCircle      },
};

const TYPE_LABEL: Record<WorkAdjustmentType, string> = {
  OVERTIME:         "Overtime",
  HOLIDAY_WORK:     "Holiday Work",
  HOLIDAY_OVERTIME: "Holiday + Overtime",
};

const HOLIDAY_TYPE_LABEL: Record<string, string> = {
  REGULAR_HOLIDAY:     "Regular Holiday (200%)",
  SPECIAL_NON_WORKING: "Special Non-Working (130%)",
};

const INPUT_CLASS =
  "w-full rounded-lg border border-[#e2e8f0] bg-[#f8fafc] px-3 py-2.5 text-sm text-[#0f172a] placeholder:text-[#94a3b8] focus:bg-white focus:border-[#2447d9] focus:outline-none focus:ring-2 focus:ring-[#2447d9]/20";
const LABEL_CLASS = "block text-xs font-medium text-[#374151] mb-1";

// ── Approve Modal ─────────────────────────────────────────────────────────────

function ApproveModal({
  item,
  businessId,
  onClose,
}: {
  item: WorkAdjustmentRequest;
  businessId: string;
  onClose: () => void;
}) {
  const needsHolidayType = item.type === "HOLIDAY_WORK" || item.type === "HOLIDAY_OVERTIME";
  const review = useReviewWorkAdjustment(businessId);

  const schema = useMemo(
    () =>
      z.object({
        holidayType: needsHolidayType
          ? z.enum(["REGULAR_HOLIDAY", "SPECIAL_NON_WORKING"], {
              required_error: "Holiday type is required",
              invalid_type_error: "Holiday type is required",
            })
          : z.enum(["REGULAR_HOLIDAY", "SPECIAL_NON_WORKING"]).optional(),
      }),
    [needsHolidayType],
  );

  const { register, handleSubmit, formState: { errors } } = useForm<{ holidayType?: string }>({
    resolver: zodResolver(schema),
  });

  const onSubmit = (data: { holidayType?: string }) => {
    review.mutate(
      {
        userId: item.userId,
        requestId: item.requestId,
        action: "approve",
        holidayType: data.holidayType as HolidayType | undefined,
      },
      { onSuccess: onClose },
    );
  };

  return (
    <Modal isOpen onClose={onClose} title="Approve Work Time Claim" size="sm">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div className="rounded-xl bg-[#f8fafc] border border-[#e2e8f0] px-4 py-3 space-y-1">
          <p className="text-xs text-[#94a3b8]">Request</p>
          <p className="text-sm font-semibold text-[#0f172a]">{item.employeeName ?? item.userId}</p>
          <p className="text-xs text-[#64748b]">
            {TYPE_LABEL[item.type]} · {format(new Date(item.workDate), "MMM d, yyyy")} · Period {item.period}
            {item.overtimeHours ? ` · ${item.overtimeHours}h OT` : ""}
          </p>
        </div>

        {needsHolidayType && (
          <div>
            <label className={LABEL_CLASS}>
              Holiday Type <span className="text-[#dc2626]">*</span>
            </label>
            <select {...register("holidayType")} className={INPUT_CLASS}>
              <option value="">Select holiday type…</option>
              <option value="REGULAR_HOLIDAY">Regular Holiday (200% rate)</option>
              <option value="SPECIAL_NON_WORKING">Special Non-Working Day (130% rate)</option>
            </select>
            {errors.holidayType && (
              <p className="mt-1 text-xs text-[#dc2626]">{errors.holidayType.message}</p>
            )}
          </div>
        )}

        <div className="rounded-lg bg-[#eff3ff] border border-[#c7d2fe] px-4 py-3 text-xs text-[#2447d9]">
          The applicable premium pay will be included in the employee's next payroll.
        </div>

        {review.isError && (
          <p className="rounded-lg bg-[#fef2f2] px-3 py-2 text-sm text-[#dc2626]">
            {(review.error as Error)?.message?.includes("ALREADY_REVIEWED")
              ? "This request has already been reviewed."
              : "Something went wrong. Please try again."}
          </p>
        )}

        <div className="flex gap-3 pt-1">
          <Button variant="secondary" onClick={onClose} fullWidth>Cancel</Button>
          <button
            type="submit"
            disabled={review.isPending}
            className="flex-1 flex items-center justify-center gap-2 rounded-lg bg-[#16a34a] px-4 py-2 text-sm font-medium text-white hover:bg-[#15803d] disabled:opacity-50 transition-colors"
          >
            {review.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            Approve
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ── Reject Modal ──────────────────────────────────────────────────────────────

const rejectSchema = z.object({
  rejectionReason: z.string().max(500).optional(),
});
type RejectForm = z.infer<typeof rejectSchema>;

function RejectModal({
  item,
  businessId,
  onClose,
}: {
  item: WorkAdjustmentRequest;
  businessId: string;
  onClose: () => void;
}) {
  const review = useReviewWorkAdjustment(businessId);
  const { register, handleSubmit } = useForm<RejectForm>({ resolver: zodResolver(rejectSchema) });

  const onSubmit = (data: RejectForm) => {
    review.mutate(
      {
        userId: item.userId,
        requestId: item.requestId,
        action: "reject",
        rejectionReason: data.rejectionReason,
      },
      { onSuccess: onClose },
    );
  };

  return (
    <Modal isOpen onClose={onClose} title="Reject Work Time Claim" size="sm">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div className="rounded-xl bg-[#f8fafc] border border-[#e2e8f0] px-4 py-3">
          <p className="text-xs text-[#94a3b8] mb-0.5">Request</p>
          <p className="text-sm font-semibold text-[#0f172a]">{item.employeeName ?? item.userId}</p>
          <p className="text-xs text-[#64748b]">
            {TYPE_LABEL[item.type]} · {format(new Date(item.workDate), "MMM d, yyyy")}
          </p>
        </div>
        <div>
          <label className={LABEL_CLASS}>
            Reason <span className="text-[#94a3b8] font-normal">(optional)</span>
          </label>
          <textarea
            rows={3}
            placeholder="Explain why the request is being rejected…"
            className={INPUT_CLASS + " resize-none"}
            {...register("rejectionReason")}
          />
        </div>
        {review.isError && (
          <p className="rounded-lg bg-[#fef2f2] px-3 py-2 text-sm text-[#dc2626]">
            Something went wrong. Please try again.
          </p>
        )}
        <div className="flex gap-3 pt-1">
          <Button variant="secondary" onClick={onClose} fullWidth>Cancel</Button>
          <button
            type="submit"
            disabled={review.isPending}
            className="flex-1 flex items-center justify-center gap-2 rounded-lg bg-[#dc2626] px-4 py-2 text-sm font-medium text-white hover:bg-[#b91c1c] disabled:opacity-50 transition-colors"
          >
            {review.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            Reject
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ── Work Adj Row ──────────────────────────────────────────────────────────────

function WorkAdjRow({
  item,
  businessId,
  activeStatus,
}: {
  item: WorkAdjustmentRequest;
  businessId: string;
  activeStatus: WorkAdjustmentStatus;
}) {
  const [expanded, setExpanded] = useState(false);
  const [modal, setModal] = useState<"approve" | "reject" | null>(null);
  const cfg = STATUS_CONFIG[item.status];

  const { canApproveAction } = useAuth();
  const canApprove = canApproveAction("work-adjustments", "approve");
  const canReject = canApproveAction("work-adjustments", "reject");

  return (
    <>
      <div className={`rounded-xl border border-[#e2e8f0] border-l-4 ${cfg.border} bg-white overflow-hidden shadow-sm`}>
        <div className="px-5 py-4 flex items-start justify-between gap-4">
          <div className="flex items-start gap-3 min-w-0">
            <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${cfg.badge}`}>
              {item.status === "PENDING"  && <Clock className="h-4 w-4" />}
              {item.status === "APPROVED" && <CheckCircle2 className="h-4 w-4" />}
              {item.status === "REJECTED" && <XCircle className="h-4 w-4" />}
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                <p className="font-semibold text-[#0f172a]">{item.employeeName ?? item.userId}</p>
                <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${cfg.badge}`}>
                  {TYPE_LABEL[item.type]}
                </span>
              </div>
              <p className="text-xs text-[#64748b]">
                {format(new Date(item.workDate), "MMM d, yyyy")} · Period {item.period}
                {item.overtimeHours ? ` · ${item.overtimeHours}h OT` : ""}
              </p>
              {item.status === "APPROVED" && item.holidayType && (
                <p className="text-xs text-[#16a34a] mt-0.5">
                  {HOLIDAY_TYPE_LABEL[item.holidayType] ?? item.holidayType}
                </p>
              )}
              {item.status === "APPROVED" && item.payslipId && (
                <p className="text-xs text-[#94a3b8] mt-0.5">Included in payslip</p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">
            {activeStatus === "PENDING" && (
              <>
                <button
                  onClick={() => setModal("approve")}
                  disabled={!canApprove}
                  title={!canApprove ? "You don't have permission to approve" : undefined}
                  className="rounded-lg bg-[#16a34a] px-3 py-1.5 text-xs font-medium text-white hover:bg-[#15803d] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Approve
                </button>
                <button
                  onClick={() => setModal("reject")}
                  disabled={!canReject}
                  title={!canReject ? "You don't have permission to reject" : undefined}
                  className="rounded-lg border border-[#fecaca] bg-[#fef2f2] px-3 py-1.5 text-xs font-medium text-[#dc2626] hover:bg-[#fee2e2] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Reject
                </button>
              </>
            )}
            <button
              type="button"
              onClick={() => setExpanded((x) => !x)}
              className="flex h-7 w-7 items-center justify-center rounded-lg border border-[#e2e8f0] text-[#94a3b8] hover:bg-[#f1f5f9] transition-colors"
            >
              {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </button>
          </div>
        </div>

        {expanded && (
          <div className="border-t border-[#f1f5f9] px-5 py-3 bg-[#f8fafc] space-y-1.5 text-sm">
            <div className="flex justify-between gap-4">
              <span className="text-[#94a3b8] shrink-0">Description</span>
              <span className="text-[#374151] text-right">{item.description}</span>
            </div>
            {item.reviewedAt && (
              <div className="flex justify-between">
                <span className="text-[#94a3b8]">Reviewed on</span>
                <span className="text-[#374151]">{format(new Date(item.reviewedAt), "MMM d, yyyy")}</span>
              </div>
            )}
            {item.reviewedBy && (
              <div className="flex justify-between">
                <span className="text-[#94a3b8]">Reviewed by</span>
                <span className="text-[#374151]">{item.reviewedBy}</span>
              </div>
            )}
            {item.rejectionReason && (
              <div className="flex justify-between gap-4">
                <span className="text-[#94a3b8] shrink-0">Rejection reason</span>
                <span className="text-[#dc2626] text-right">{item.rejectionReason}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-[#94a3b8]">Submitted on</span>
              <span className="text-[#374151]">{format(new Date(item.createdAt), "MMM d, yyyy")}</span>
            </div>
          </div>
        )}
      </div>

      {modal === "approve" && (
        <ApproveModal item={item} businessId={businessId} onClose={() => setModal(null)} />
      )}
      {modal === "reject" && (
        <RejectModal item={item} businessId={businessId} onClose={() => setModal(null)} />
      )}
    </>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function AdminWorkAdjustmentsPage() {
  const { activeBusinessId } = useAuth();
  const businessId = activeBusinessId ?? "";

  const [activeTab, setActiveTab] = useState<WorkAdjustmentStatus>("PENDING");
  const [knownCounts, setKnownCounts] = useState<Partial<Record<WorkAdjustmentStatus, number>>>({});

  const { data, isLoading, error, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useAdminWorkAdjustments(businessId, activeTab, !!businessId);

  const items = useMemo(() => data?.pages.flatMap((p) => p.items) ?? [], [data]);

  useEffect(() => {
    if (!isLoading && data !== undefined) {
      setKnownCounts((prev) => ({ ...prev, [activeTab]: items.length }));
    }
  }, [isLoading, data, activeTab, items.length]);

  return (
    <div className="space-y-5">

      {/* ── Header card ───────────────────────────────────────────────────── */}
      <div className="rounded-xl border border-[#e2e8f0] bg-white px-5 py-4 shadow-sm">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-2.5">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#2447d9]">
              <Timer className="h-4 w-4 text-white" />
            </div>
            <div>
              <h1 className="text-base font-bold text-[#0f172a]">Manage Work Time Claims</h1>
              <p className="text-xs text-[#64748b]">Review overtime and holiday pay requests</p>
            </div>
          </div>
          {activeTab === "PENDING" && !isLoading && items.length > 0 && (
            <div className="rounded-lg bg-[#fef9c3] border border-[#fde68a] px-3 py-1.5 text-center">
              <p className="text-xs text-[#a16207] font-medium">
                {items.length} request{items.length !== 1 ? "s" : ""} pending
              </p>
            </div>
          )}
        </div>
      </div>

      {/* ── Tabs ──────────────────────────────────────────────────────────── */}
      <div className="flex gap-1 rounded-lg bg-[#f1f5f9] p-1 w-fit overflow-x-auto overflow-y-hidden">
        {STATUS_TABS.map((tab) => {
          const count = knownCounts[tab.key];
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-1.5 rounded-md px-4 py-1.5 text-sm font-medium transition-colors whitespace-nowrap ${
                activeTab === tab.key
                  ? "bg-white text-[#0f172a] shadow-sm"
                  : "text-[#64748b] hover:text-[#0f172a]"
              }`}
            >
              {tab.label}
              {count !== undefined && count > 0 && (
                <span
                  className={`text-xs rounded-full px-1.5 py-0.5 font-semibold ${
                    activeTab === tab.key
                      ? "bg-[#eff3ff] text-[#2447d9]"
                      : "bg-[#e2e8f0] text-[#64748b]"
                  }`}
                >
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* ── Content ───────────────────────────────────────────────────────── */}
      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <LoadingSpinner size="lg" className="text-[#2447d9]" />
        </div>
      ) : error ? (
        <div className="rounded-xl border border-[#fecaca] bg-[#fef2f2] px-5 py-4 text-sm text-[#dc2626]">
          Failed to load work time claims. Please try again.
        </div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-[#e2e8f0] bg-white py-16 text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#eff3ff]">
            <Timer className="h-7 w-7 text-[#2447d9]" />
          </div>
          <div>
            <p className="font-semibold text-[#0f172a]">
              No {STATUS_TABS.find((t) => t.key === activeTab)?.label.toLowerCase()} requests
            </p>
            <p className="text-xs text-[#94a3b8] mt-0.5">
              {activeTab === "PENDING"
                ? "No work time claims are waiting for your review."
                : "No work time claims with this status."}
            </p>
          </div>
        </div>
      ) : (
        <>
          <div className="space-y-3">
            {items.map((item) => (
              <WorkAdjRow
                key={item.requestId}
                item={item}
                businessId={businessId}
                activeStatus={activeTab}
              />
            ))}
          </div>
          {hasNextPage && (
            <div className="flex justify-center pt-1">
              <button
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}
                className="rounded-lg border border-[#e2e8f0] bg-white px-5 py-2 text-sm font-medium text-[#374151] hover:bg-[#f8fafc] disabled:opacity-50 transition-colors"
              >
                {isFetchingNextPage ? "Loading…" : "Load more"}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
